import {
    Add,
    CheckCircle,
    Close,
    EmojiEvents,
    Search,
    Send,
} from "@mui/icons-material";
import {
    Avatar,
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Paper,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Button from "../../../components/ui/Button";
import apiService from "../../../service/Api";
import { tokens } from "../../../theme";

interface CrewMember {
  id: number;
  user?: {
    first_name?: string;
    last_name?: string;
    profile_picture_url?: string | null;
  };
}

interface Role {
  id: number;
  name: string;
}

interface Stream {
  id: number;
  title?: string;
  booking: {
    deceased?: { first_name: string; surname: string }[];
    services?: { date_of_service?: string; venue?: { name: string } }[];
  };
  service?: {
    date_of_service?: string;
    venue?: { name: string };
  };
}

type AssignmentMap = Record<number, Record<number, CrewMember>>;

function crewName(c: CrewMember) {
  const f = c.user?.first_name ?? "";
  const l = c.user?.last_name ?? "";
  return `${f} ${l}`.trim() || `#${c.id}`;
}

function crewInitials(c: CrewMember) {
  const f = c.user?.first_name?.[0] ?? "";
  const l = c.user?.last_name?.[0] ?? "";
  return `${f}${l}`.toUpperCase() || "?";
}

function streamLabel(s: Stream) {
  const d = s.booking?.deceased?.[0];
  return d ? `${d.first_name} ${d.surname}` : s.title || `Stream #${s.id}`;
}

function streamDate(s: Stream) {
  const raw =
    s.service?.date_of_service || s.booking?.services?.[0]?.date_of_service;
  if (!raw) return "Date TBD";
  return new Date(raw).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function streamVenue(s: Stream) {
  return (
    s.service?.venue?.name ||
    s.booking?.services?.[0]?.venue?.name ||
    "Venue TBD"
  );
}

function useDebounce(value: string, delay = 400) {
  const [d, setD] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setD(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return d;
}

const AVATAR_COLORS = [
  "#5c5cd6",
  "#d97706",
  "#0891b2",
  "#7c3aed",
  "#db2777",
  "#059669",
  "#ea580c",
  "#0369a1",
];
function avatarBg(id: number) {
  return AVATAR_COLORS[id % AVATAR_COLORS.length];
}

const CrewAssignmentPage: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const surface = isDark ? colors.primary[900] : "#ffffff";
  const surfaceBorder = isDark ? colors.primary[800] : "#e5e7eb";
  const textPrimary = isDark ? colors.primary[100] : "#111827";
  const textSecondary = isDark ? colors.primary[400] : "#6b7280";

  const [roles, setRoles] = useState<Role[]>([]);
  const [crew, setCrew] = useState<CrewMember[]>([]);
  const [streams, setStreams] = useState<Stream[]>([]);

  const [crewDialogOpen, setCrewDialogOpen] = useState(false);
  const [streamDialogOpen, setStreamDialogOpen] = useState(false);
  const [crewSearch, setCrewSearch] = useState("");
  const [streamSearch, setStreamSearch] = useState("");
  const [crewResults, setCrewResults] = useState<CrewMember[]>([]);
  const [streamResults, setStreamResults] = useState<Stream[]>([]);
  const [crewLoading, setCrewLoading] = useState(false);
  const [streamLoading, setStreamLoading] = useState(false);

  const [draggingCrew, setDraggingCrew] = useState<CrewMember | null>(null);
  const [dragOverSlot, setDragOverSlot] = useState<{
    streamId: number;
    roleId: number;
  } | null>(null);

  const [assignments, setAssignments] = useState<AssignmentMap>({});

  const [submitting, setSubmitting] = useState(false);

  const debouncedCrew = useDebounce(crewSearch);
  const debouncedStream = useDebounce(streamSearch);

  useEffect(() => {
    apiService.get<{ results: Role[] }>("/api/crews/roles/").then((r) => {
      setRoles(r.data.results || []);
    });
  }, []);

  useEffect(() => {
    if (!debouncedCrew) {
      setCrewResults([]);
      return;
    }
    setCrewLoading(true);
    apiService
      .get<{ results: CrewMember[] }>("/api/crews/crew-members/", {
        search: debouncedCrew,
        page_size: 30,
      })
      .then((r) => setCrewResults(r.data.results || []))
      .catch(() => setCrewResults([]))
      .finally(() => setCrewLoading(false));
  }, [debouncedCrew]);

  useEffect(() => {
    if (!debouncedStream) {
      setStreamResults([]);
      return;
    }
    setStreamLoading(true);
    apiService
      .get<{ results: Stream[] }>("/api/streams/stream/", {
        search: debouncedStream,
        page_size: 20,
      })
      .then((r) => setStreamResults(r.data.results || []))
      .catch(() => setStreamResults([]))
      .finally(() => setStreamLoading(false));
  }, [debouncedStream]);

  const addCrewMember = (c: CrewMember) => {
    if (!crew.find((x) => x.id === c.id)) setCrew((prev) => [...prev, c]);
  };

  const addStream = (s: Stream) => {
    if (!streams.find((x) => x.id === s.id)) {
      setStreams((prev) => [...prev, s]);
      setAssignments((prev) => ({ ...prev, [s.id]: {} }));
    }
  };

  const removeStream = (streamId: number) => {
    setStreams((prev) => prev.filter((s) => s.id !== streamId));
    setAssignments((prev) => {
      const n = { ...prev };
      delete n[streamId];
      return n;
    });
  };

  const handleDrop = useCallback(
    (streamId: number, roleId: number) => {
      if (!draggingCrew) return;
      setAssignments((prev) => ({
        ...prev,
        [streamId]: { ...(prev[streamId] ?? {}), [roleId]: draggingCrew },
      }));
      setDragOverSlot(null);
    },
    [draggingCrew],
  );

  const removeAssignment = (streamId: number, roleId: number) => {
    setAssignments((prev) => {
      const n = { ...prev };
      if (n[streamId]) {
        delete n[streamId][roleId];
      }
      return n;
    });
  };

  const totalFilled = Object.values(assignments).reduce(
    (sum, byRole) => sum + Object.values(byRole).filter(Boolean).length,
    0,
  );

  const handleSubmit = async () => {
    if (totalFilled === 0) return;
    const payload: {
      stream_id: number;
      crew_member_id: number;
      assignment: { role: string; notes: string }[];
    }[] = [];

    Object.entries(assignments).forEach(([streamId, byRole]) => {
      Object.entries(byRole).forEach(([roleId, c]) => {
        if (!c) return;
        const role = roles.find((r) => r.id === Number(roleId));
        if (!role) return;
        payload.push({
          stream_id: Number(streamId),
          crew_member_id: c.id,
          assignment: [{ role: role.name, notes: "" }],
        });
      });
    });

    setSubmitting(true);
    try {
      await apiService.post("/api/call-sheets/callsheet/bulk-create/", payload);

      toast.success(`${payload.length} assignment(s) confirmed!`);

      setAssignments({});
    } catch {
      toast.error("Failed to submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const filledCount = (streamId: number) =>
    roles.filter((r) => assignments[streamId]?.[r.id]).length;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        gap: 0,
      }}
    >
      {/* ══ HEADER ══ */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          flexShrink: 0,
        }}
      >
        <Typography variant="h5" fontWeight={800} color={textPrimary}>
          Crew Assignment
        </Typography>

        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          {totalFilled > 0 && (
            <Typography
              variant="caption"
              color={textSecondary}
              fontWeight={600}
            >
              {totalFilled} assignment{totalFilled !== 1 ? "s" : ""} pending
            </Typography>
          )}
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={totalFilled === 0 || submitting}
            startIcon={
              submitting ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <Send sx={{ fontSize: 14 }} />
              )
            }
            sx={{
              fontWeight: 700,
              borderRadius: "10px",
              px: 2.5,
              fontSize: "0.82rem",
            }}
          >
            Confirm
          </Button>
        </Box>
      </Box>

      {/* ══ CREW ROW ══ */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "20px",
          p: 2,
          mb: 2.5,
          backgroundColor: surface,
          border: `1px solid ${surfaceBorder}`,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* Label */}
          <Typography
            variant="caption"
            fontWeight={800}
            color={textSecondary}
            sx={{
              letterSpacing: "0.08em",
              fontSize: "0.65rem",
              whiteSpace: "nowrap",
            }}
          >
            CREW
          </Typography>

          {/* Avatar strip */}
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              flexGrow: 1,
              overflowX: "auto",
              py: 0.5,
              "&::-webkit-scrollbar": { height: 3 },
              "&::-webkit-scrollbar-thumb": {
                borderRadius: 2,
                bgcolor: surfaceBorder,
              },
            }}
          >
            {crew.map((c) => {
              const assignedCount = Object.values(assignments).filter(
                (byRole) => Object.values(byRole).some((a) => a?.id === c.id),
              ).length;

              return (
                <Tooltip key={c.id} title={crewName(c)} arrow>
                  <Box
                    draggable
                    onDragStart={() => setDraggingCrew(c)}
                    onDragEnd={() => setDraggingCrew(null)}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.5,
                      cursor: "grab",
                      userSelect: "none",
                      flexShrink: 0,
                      opacity: draggingCrew?.id === c.id ? 0.4 : 1,
                      transition: "transform 0.15s ease, opacity 0.15s",
                      "&:hover": { transform: "translateY(-5px)" },
                      "&:active": { cursor: "grabbing" },
                    }}
                  >
                    <Box sx={{ position: "relative" }}>
                      <Avatar
                        src={c.user?.profile_picture_url ?? undefined}
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor: avatarBg(c.id),
                          fontSize: "0.82rem",
                          fontWeight: 700,
                          border: `3px solid ${assignedCount > 0 ? "#22c55e" : surfaceBorder}`,
                          transition: "border-color 0.2s ease",
                        }}
                      >
                        {crewInitials(c)}
                      </Avatar>
                      {assignedCount > 0 && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: -3,
                            right: -3,
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            bgcolor: "#22c55e",
                            border: `2px solid ${surface}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.5rem",
                              fontWeight: 800,
                              color: "#fff",
                            }}
                          >
                            {assignedCount}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{
                        fontSize: "0.6rem",
                        color: textSecondary,
                        maxWidth: 52,
                        textAlign: "center",
                        fontWeight: 600,
                      }}
                    >
                      {c.user?.first_name || crewName(c)}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}

            {/* Add crew button */}
            <Box
              onClick={() => setCrewDialogOpen(true)}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
                flexShrink: 0,
                "&:hover .add-ring": {
                  borderColor: "#5c5cd6",
                  transform: "scale(1.08)",
                },
              }}
            >
              <Box
                className="add-ring"
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  border: `2.5px dashed ${surfaceBorder}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s ease",
                }}
              >
                <Add sx={{ fontSize: 20, color: textSecondary }} />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.6rem",
                  color: textSecondary,
                  fontWeight: 600,
                }}
              >
                Add
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* ══ STREAM COLUMNS ══ */}
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          gap: 2,
          overflowX: "auto",
          overflowY: "hidden",
          pb: 1,
          alignItems: "flex-start",
          "&::-webkit-scrollbar": { height: 5 },
          "&::-webkit-scrollbar-thumb": {
            borderRadius: 3,
            bgcolor: surfaceBorder,
          },
        }}
      >
        {streams.map((stream) => {
          const filled = filledCount(stream.id);
          const total = roles.length;
          const allFilled = filled === total && total > 0;

          return (
            <Box
              key={stream.id}
              sx={{
                minWidth: 240,
                maxWidth: 280,
                flex: "0 0 260px",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              {/* Stream header card */}
              <Paper
                elevation={0}
                sx={{
                  borderRadius: "16px",
                  p: 1.75,
                  backgroundColor: surface,
                  border: `1px solid ${allFilled ? "#22c55e55" : surfaceBorder}`,
                  position: "relative",
                  transition: "border-color 0.3s ease",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color={textPrimary}
                      noWrap
                      sx={{ fontSize: "0.85rem" }}
                    >
                      {streamLabel(stream)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color={textSecondary}
                      sx={{ fontSize: "0.68rem" }}
                    >
                      {streamDate(stream)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color={textSecondary}
                      noWrap
                      display="block"
                      sx={{ fontSize: "0.68rem" }}
                    >
                      {streamVenue(stream)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 0.5,
                    }}
                  >
                    {allFilled && (
                      <EmojiEvents sx={{ fontSize: 18, color: "#22c55e" }} />
                    )}
                    <IconButton
                      size="small"
                      onClick={() => removeStream(stream.id)}
                      sx={{
                        p: 0.25,
                        color: textSecondary,
                        "&:hover": { color: "#ef4444" },
                      }}
                    >
                      <Close sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                </Box>

                {/* Progress dots */}
                <Box sx={{ display: "flex", gap: 0.5, mt: 1.25 }}>
                  {roles.map((r) => (
                    <Box
                      key={r.id}
                      sx={{
                        height: 4,
                        flex: 1,
                        borderRadius: 2,
                        bgcolor: assignments[stream.id]?.[r.id]
                          ? "#22c55e"
                          : isDark
                            ? colors.primary[700]
                            : "#e5e7eb",
                        transition: "background-color 0.3s ease",
                      }}
                    />
                  ))}
                </Box>
                <Typography
                  variant="caption"
                  color={allFilled ? "#22c55e" : textSecondary}
                  sx={{
                    fontSize: "0.62rem",
                    mt: 0.5,
                    display: "block",
                    fontWeight: 600,
                  }}
                >
                  {filled}/{total} roles filled
                </Typography>
              </Paper>

              {/* Role slot cards */}
              {roles.map((role) => {
                const assigned = assignments[stream.id]?.[role.id] ?? null;
                const isOver =
                  dragOverSlot?.streamId === stream.id &&
                  dragOverSlot?.roleId === role.id;

                return (
                  <Paper
                    key={role.id}
                    elevation={0}
                    onDragOver={(e) => {
                      if (draggingCrew) {
                        e.preventDefault();
                        setDragOverSlot({
                          streamId: stream.id,
                          roleId: role.id,
                        });
                      }
                    }}
                    onDragLeave={() => setDragOverSlot(null)}
                    onDrop={(e) => {
                      e.preventDefault();
                      handleDrop(stream.id, role.id);
                    }}
                    sx={{
                      borderRadius: "14px",
                      p: 1.5,
                      backgroundColor: isOver
                        ? isDark
                          ? "rgba(92,92,214,0.15)"
                          : "rgba(92,92,214,0.08)"
                        : assigned
                          ? surface
                          : isDark
                            ? `${colors.primary[900]}88`
                            : "#fafafa",
                      border: isOver
                        ? "2px dashed #5c5cd6"
                        : assigned
                          ? `1px solid ${surfaceBorder}`
                          : `1.5px dashed ${isDark ? colors.primary[700] : "#d1d5db"}`,
                      transition: "all 0.18s ease",
                      transform: isOver ? "scale(1.02)" : "scale(1)",
                      cursor: draggingCrew ? "copy" : "default",
                      animation: assigned ? "slotSnap 0.3s ease" : "none",
                      "@keyframes slotSnap": {
                        "0%": { transform: "scale(0.97)" },
                        "50%": { transform: "scale(1.03)" },
                        "100%": { transform: "scale(1)" },
                      },
                    }}
                  >
                    {/* Role chip */}
                    <Chip
                      label={role.name}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.6rem",
                        fontWeight: 700,
                        mb: 1,
                        bgcolor: assigned
                          ? isDark
                            ? "#5c5cd622"
                            : "#ede9fe"
                          : isDark
                            ? colors.primary[800]
                            : "#f3f4f6",
                        color: assigned ? "#5c5cd6" : textSecondary,
                      }}
                    />

                    {assigned ? (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Avatar
                          src={assigned.user?.profile_picture_url ?? undefined}
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: avatarBg(assigned.id),
                            fontSize: "0.68rem",
                            fontWeight: 700,
                          }}
                        >
                          {crewInitials(assigned)}
                        </Avatar>
                        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            color={textPrimary}
                            noWrap
                            sx={{ fontSize: "0.75rem", display: "block" }}
                          >
                            {crewName(assigned)}
                          </Typography>
                        </Box>
                        <CheckCircle
                          sx={{ color: "#22c55e", fontSize: 15, flexShrink: 0 }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => removeAssignment(stream.id, role.id)}
                          sx={{
                            p: 0.25,
                            color: textSecondary,
                            "&:hover": { color: "#ef4444" },
                            flexShrink: 0,
                          }}
                        >
                          <Close sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Box>
                    ) : (
                      <Typography
                        variant="caption"
                        sx={{
                          color: isOver ? "#5c5cd6" : textSecondary,
                          fontSize: "0.7rem",
                          fontWeight: isOver ? 600 : 400,
                        }}
                      >
                        {isOver ? "Release to assign" : "Drop crew here"}
                      </Typography>
                    )}
                  </Paper>
                );
              })}
            </Box>
          );
        })}

        {/* Add stream column */}
        <Box
          onClick={() => setStreamDialogOpen(true)}
          sx={{
            minWidth: 200,
            flex: "0 0 200px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            borderRadius: "20px",
            border: `2px dashed ${surfaceBorder}`,
            p: 3,
            cursor: "pointer",
            transition: "all 0.2s ease",
            alignSelf: "stretch",
            "&:hover": {
              borderColor: "#5c5cd6",
              backgroundColor: isDark
                ? "rgba(92,92,214,0.06)"
                : "rgba(92,92,214,0.04)",
            },
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              bgcolor: isDark ? colors.primary[800] : "#f3f4f6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Add sx={{ fontSize: 22, color: textSecondary }} />
          </Box>
          <Typography
            variant="body2"
            color={textSecondary}
            fontWeight={600}
            sx={{ fontSize: "0.8rem", textAlign: "center" }}
          >
            Add Event
          </Typography>
        </Box>
      </Box>

      {/* ══ ADD CREW DIALOG ══ */}
      <Dialog
        open={crewDialogOpen}
        onClose={() => {
          setCrewDialogOpen(false);
          setCrewSearch("");
          setCrewResults([]);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          Add Crew to Board
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            placeholder="Search by name…"
            value={crewSearch}
            onChange={(e) => setCrewSearch(e.target.value)}
            size="small"
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
              endAdornment: crewLoading ? (
                <InputAdornment position="end">
                  <CircularProgress size={14} />
                </InputAdornment>
              ) : null,
            }}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              maxHeight: 320,
              overflowY: "auto",
            }}
          >
            {crewResults.length === 0 && crewSearch && !crewLoading && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", py: 2 }}
              >
                No results
              </Typography>
            )}
            {crewResults.map((c) => {
              const already = crew.some((x) => x.id === c.id);
              return (
                <Box
                  key={c.id}
                  onClick={() => {
                    if (!already) {
                      addCrewMember(c);
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1,
                    borderRadius: "10px",
                    cursor: already ? "default" : "pointer",
                    opacity: already ? 0.5 : 1,
                    "&:hover": {
                      bgcolor: already ? "transparent" : "action.hover",
                    },
                  }}
                >
                  <Avatar
                    src={c.user?.profile_picture_url ?? undefined}
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: avatarBg(c.id),
                      fontSize: "0.75rem",
                      fontWeight: 700,
                    }}
                  >
                    {crewInitials(c)}
                  </Avatar>
                  <Typography variant="body2" fontWeight={600}>
                    {crewName(c)}
                  </Typography>
                  {already && (
                    <Chip
                      label="Added"
                      size="small"
                      sx={{ ml: "auto", height: 20, fontSize: "0.6rem" }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        </DialogContent>
      </Dialog>

      {/* ══ ADD STREAM DIALOG ══ */}
      <Dialog
        open={streamDialogOpen}
        onClose={() => {
          setStreamDialogOpen(false);
          setStreamSearch("");
          setStreamResults([]);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Add Event</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            placeholder="Search by name or date…"
            value={streamSearch}
            onChange={(e) => setStreamSearch(e.target.value)}
            size="small"
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
              endAdornment: streamLoading ? (
                <InputAdornment position="end">
                  <CircularProgress size={14} />
                </InputAdornment>
              ) : null,
            }}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              maxHeight: 320,
              overflowY: "auto",
            }}
          >
            {streamResults.length === 0 && streamSearch && !streamLoading && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", py: 2 }}
              >
                No results
              </Typography>
            )}
            {streamResults.map((s) => {
              const already = streams.some((x) => x.id === s.id);
              return (
                <Box
                  key={s.id}
                  onClick={() => {
                    if (!already) {
                      addStream(s);
                      setStreamDialogOpen(false);
                      setStreamSearch("");
                      setStreamResults([]);
                    }
                  }}
                  sx={{
                    p: 1.25,
                    borderRadius: "10px",
                    cursor: already ? "default" : "pointer",
                    opacity: already ? 0.5 : 1,
                    border: "1px solid",
                    borderColor: "divider",
                    "&:hover": {
                      bgcolor: already ? "transparent" : "action.hover",
                    },
                  }}
                >
                  <Typography variant="body2" fontWeight={700}>
                    {streamLabel(s)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {streamDate(s)} · {streamVenue(s)}
                  </Typography>
                  {already && (
                    <Chip
                      label="Added"
                      size="small"
                      sx={{ ml: 1, height: 18, fontSize: "0.6rem" }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CrewAssignmentPage;
